import { CommissionBackendResponse, CommissionCategoryTypeEnum } from "../modules/shared/model";

/**
 * Calculate commission for a withdrawal amount
 */
export const calculateCommission = (
  amount: number,
  commissionRule: CommissionBackendResponse | null,
  userTotalRevenue?: number
): {
  baseCommission: number;
  tierCommission: number;
  appliedTierRate: number;
  netAmount: number;
  commissionAmount: number;
  tierName: string;
} => {
  if (!commissionRule) {
    return {
      baseCommission: 0,
      tierCommission: 0,
      appliedTierRate: 0,
      netAmount: amount,
      commissionAmount: 0,
      tierName: "No Tier",
    };
  }

  const baseRate = commissionRule.baseRate || 0;
  const baseCommission = (amount * baseRate) / 100;

  // Find applicable tier based on user's total revenue or withdrawal amount
  const revenue = userTotalRevenue || amount;
  let appliedTier = null;

  if (commissionRule.commissionTiers && commissionRule.commissionTiers.length > 0) {
    // Sort tiers by threshold (highest to lowest)
    const sortedTiers = [...commissionRule.commissionTiers].sort(
      (a, b) => (b.threshold || 0) - (a.threshold || 0)
    );

    // Find the first tier where revenue >= threshold
    for (const tier of sortedTiers) {
      if (revenue >= (tier.threshold || 0)) {
        appliedTier = tier;
        break;
      }
    }
  }

  if (appliedTier) {
    const tierRate = appliedTier.rate || 0;
    const tierCommission = (amount * tierRate) / 100;
    const commissionAmount = tierCommission;

    return {
      baseCommission,
      tierCommission,
      appliedTierRate: tierRate,
      netAmount: amount - commissionAmount,
      commissionAmount,
      tierName: appliedTier.name?.name || "Unknown Tier",
    };
  }

  // No tier applies, use base rate
  return {
    baseCommission,
    tierCommission: baseCommission,
    appliedTierRate: baseRate,
    netAmount: amount - baseCommission,
    commissionAmount: baseCommission,
    tierName: "Base Rate",
  };
};

/**
 * Check if user meets minimum payout threshold
 */
export const meetsMinimumPayout = (
  availableBalance: number,
  commissionRule: CommissionBackendResponse | null
): { meets: boolean; minimumPayout: number; shortfall: number } => {
  if (!commissionRule) {
    return { meets: true, minimumPayout: 0, shortfall: 0 };
  }

  const minimumPayout = commissionRule.minimumPayout || 0;
  const meets = availableBalance >= minimumPayout;
  const shortfall = meets ? 0 : minimumPayout - availableBalance;

  return { meets, minimumPayout, shortfall };
};

/**
 * Get applicable commission rule for user based on their actual transaction category
 * 
 * Priority:
 * 1. Check category from released transactions (actual work done)
 * 2. Check user's role (fallback)
 * 3. Return first active rule (default)
 */
export const getApplicableCommissionRule = (
  commissionRules: CommissionBackendResponse[],
  userRole?: number,
  releasedTransactions?: any[]
): CommissionBackendResponse | null => {
  if (!commissionRules || commissionRules.length === 0) {
    return null;
  }

  // Priority 1: Check category from released transactions (actual work done)
  // This is the most accurate - based on what they actually earned money from
  if (releasedTransactions && releasedTransactions.length > 0) {
    // Get the first released transaction
    const firstReleasedTransaction = releasedTransactions[0];
    
    // Extract category type: contract -> offer -> job -> categoryId -> type.id
    const categoryTypeId = firstReleasedTransaction?.contract?.offer?.job?.categoryId?.type?.id;
    
    if (categoryTypeId) {
      // Convert to number for comparison
      const numericCategoryTypeId = Number(categoryTypeId);
      
      // Find matching commission rule
      // Job CategoryTypeEnum: 1=Home_Service, 2=Professional_Service, 3=International_Service
      // Commission CategoryTypeEnum: 1=Local_Service, 2=Professional_Service, 3=International_Service, 4=Emergency_Service
      const matchingRule = commissionRules.find(
        (rule) => Number(rule.categoryType?.id) === numericCategoryTypeId && rule.status?.id === "1"
      );
      
      if (matchingRule) {
        return matchingRule;
      }
    }
  }

  // Priority 2: Fallback to user role mapping
  let categoryTypeId: string | null = null;

  // RoleEnum: professional = 4, internationl_professional = 5, emergency_professional = 6
  if (userRole === 4) {
    // Professional - could be Local or Professional Service
    categoryTypeId = String(CommissionCategoryTypeEnum.Professional_Service);
  } else if (userRole === 5) {
    // International Professional
    categoryTypeId = String(CommissionCategoryTypeEnum.International_Service);
  } else if (userRole === 6) {
    // Emergency Professional
    categoryTypeId = String(CommissionCategoryTypeEnum.Emergency_Service);
  }

  // Try to find commission rule matching the category
  if (categoryTypeId) {
    const matchingRule = commissionRules.find(
      (rule) => rule.categoryType?.id === categoryTypeId && rule.status?.id === "1" // Active
    );
    if (matchingRule) {
      return matchingRule;
    }
  }

  // Priority 3: Fallback - return first active rule
  const activeRule = commissionRules.find((rule) => rule.status?.id === "1");
  return activeRule || null;
};

/**
 * Format commission tier display
 */
export const formatCommissionTier = (tier: any): string => {
  if (!tier) return "N/A";
  return `${tier.name?.name || "Unknown"} (${tier.rate}% at $${tier.threshold}+)`;
};

/**
 * Get next billing cycle date
 */
export const getNextBillingCycleDate = (
  billingCycle: { id: string; name: string } | null
): Date => {
  const now = new Date();

  if (!billingCycle) {
    // Default to monthly
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  switch (billingCycle.id) {
    case "1": // Monthly
      return new Date(now.getFullYear(), now.getMonth() + 1, 1);
    case "2": // Yearly
      return new Date(now.getFullYear() + 1, 0, 1);
    case "3": { // Weekly
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);
      return nextWeek;
    }
    default:
      return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }
};

/**
 * Check if withdrawal is allowed based on billing cycle
 * Returns { allowed: boolean, reason: string, nextAvailableDate: Date }
 */
export const checkBillingCycleAllowsWithdrawal = (
  billingCycle: { id: string; name: string } | null,
  lastWithdrawalDate?: string | null,
  withdrawalHistory?: any[]
): { allowed: boolean; reason?: string; nextAvailableDate?: Date } => {
  if (!billingCycle) {
    // No billing cycle restriction
    return { allowed: true };
  }

  const now = new Date();
  
  // If no withdrawal history provided, check last withdrawal date
  if (!withdrawalHistory || withdrawalHistory.length === 0) {
    if (!lastWithdrawalDate) {
      // No previous withdrawal, allow
      return { allowed: true };
    }

    const lastWithdrawal = new Date(lastWithdrawalDate);
    
    switch (billingCycle.id) {
      case "1": { // Monthly
        // Check if last withdrawal was in current month
        const isSameMonth = lastWithdrawal.getMonth() === now.getMonth() && 
                           lastWithdrawal.getFullYear() === now.getFullYear();
        
        if (isSameMonth) {
          const nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          return {
            allowed: false,
            reason: `You have already withdrawn this month. Next withdrawal available on ${nextDate.toLocaleDateString()}.`,
            nextAvailableDate: nextDate
          };
        }
        return { allowed: true };
      }

      case "2": { // Yearly
        // Check if last withdrawal was in current year
        const isSameYear = lastWithdrawal.getFullYear() === now.getFullYear();
        
        if (isSameYear) {
          const nextDate = new Date(now.getFullYear() + 1, 0, 1);
          return {
            allowed: false,
            reason: `You have already withdrawn this year. Next withdrawal available on ${nextDate.toLocaleDateString()}.`,
            nextAvailableDate: nextDate
          };
        }
        return { allowed: true };
      }

      case "3": { // Weekly
        // Check if last withdrawal was within last 7 days
        const daysSinceLastWithdrawal = Math.floor((now.getTime() - lastWithdrawal.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysSinceLastWithdrawal < 7) {
          const nextDate = new Date(lastWithdrawal);
          nextDate.setDate(lastWithdrawal.getDate() + 7);
          return {
            allowed: false,
            reason: `You can withdraw once per week. Next withdrawal available on ${nextDate.toLocaleDateString()}.`,
            nextAvailableDate: nextDate
          };
        }
        return { allowed: true };
      }

      default:
        return { allowed: true };
    }
  }

  // If withdrawal history is provided, check most recent withdrawal
  const sortedWithdrawals = [...withdrawalHistory].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  
  const mostRecentWithdrawal = sortedWithdrawals[0];
  if (mostRecentWithdrawal) {
    return checkBillingCycleAllowsWithdrawal(
      billingCycle,
      mostRecentWithdrawal.createdAt,
      undefined
    );
  }

  return { allowed: true };
};

